import Head from 'next/head';
import { GetServerSidePropsContext } from 'next/types';

import Footer from '@components/common/Footer';
import Header from '@components/common/Header';
import PartnerComponent from '@components/partners/Partner';

import { fetchAggregatePartner, fetchFeedbackSummary } from '@utils/api';

import { GlobalData } from '@customTypes/GlobalData';
import { FeedbackSummaryDto, Partner } from '@customTypes/Partner';
import { PartnerPageTemplate } from '@customTypes/PartnerPageTemplate';
import { Regions } from '@customTypes/Region';
import { Services } from '@customTypes/Service';

type PartnerProfilePageProps = {
  partnerId: number;
  global: GlobalData;
  data: {
    partner: Partner;
    services: Services;
    regions: Regions;
    template?: PartnerPageTemplate;
    feedbackSummary: FeedbackSummaryDto;
  };
};

function PartnerProfilePage(props: PartnerProfilePageProps) {
  const { partnerId, global, data } = props;

  return (
    <>
      <Head>
        <title>{global?.siteDescription}</title>
        <link rel="icon" href={global?.favicon?.data?.attributes?.url || ''} />
        <link rel="canonical" href={`https://miso.kr/partners/${partnerId}`} />
      </Head>
      <Header data={global} pageViewer="web" />
      <PartnerComponent {...data} />
      <Footer data={global} pageViewer="web" />
    </>
  );
}

export async function getServerSideProps(
  context: GetServerSidePropsContext
): Promise<{ props: PartnerProfilePageProps } | { notFound: true }> {
  try {
    const { res } = context;
    const { id } = context.params || {};
    const { serviceId } = context.query || {};
    if (!id || Array.isArray(id) || isNaN(Number(id))) throw Error('No given slug');
    if (serviceId && isNaN(Number(serviceId))) throw Error('Wrong serviceId');
    const props: PartnerProfilePageProps = await fetchAggregatePartner(Number(id), Number(serviceId));
    const feedbackSummary: FeedbackSummaryDto = await fetchFeedbackSummary(Number(props.data.partner.supplier_id));

    if (!props.partnerId) throw Error('Not found');
    res.setHeader('Cache-Control', 'public, max-age=60, must-revalidate');
    return {
      props: { ...props, data: { ...props.data, feedbackSummary } },
    };
  } catch (e) {
    return { notFound: true };
  }
}

export default PartnerProfilePage;
